import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { appUrl } from "../api/endpoints";
import "./ForgotPassword.css";

export const AcceptInvitePassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);

  const [inviteUser, setInviteUser] = useState(null);
  const [status, setStatus] = useState({ type: "loading", message: "Checking invitation link..." });
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const passwordChecks = useMemo(
    () => ({
      minLength: password.length >= 8,
      hasLowercase: /[a-z]/.test(password),
      hasUppercase: /[A-Z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecial: /[^A-Za-z0-9]/.test(password),
    }),
    [password]
  );

  const passwordPolicyValid = Object.values(passwordChecks).every(Boolean);

  const passwordConditions = [
    { key: "minLength", label: "At least 8 characters" },
    { key: "hasLowercase", label: "At least one lowercase letter" },
    { key: "hasUppercase", label: "At least one uppercase letter" },
    { key: "hasNumber", label: "At least one number" },
    { key: "hasSpecial", label: "At least one special character" },
  ];

  useEffect(() => {
    let active = true;

    const loadInvite = async () => {
      if (!token) {
        setStatus({ type: "error", message: "Invitation token is missing." });
        return;
      }

      try {
        const response = await axios.get(appUrl("auth/invite-password"), {
          params: { token },
          withCredentials: false,
        });

        if (!active) return;
        setInviteUser(response.data?.user || null);
        setStatus({ type: "ready", message: "" });
      } catch (err) {
        if (!active) return;
        setStatus({
          type: "error",
          message: err.response?.data?.message || "Invitation link is invalid or expired.",
        });
      }
    };

    loadInvite();
    return () => {
      active = false;
    };
  }, [token]);

  const submitPassword = useCallback(
    async (event) => {
      event.preventDefault();

      if (!passwordPolicyValid) {
        setStatus({ type: "error", message: "Password does not meet all required conditions." });
        return;
      }

      if (password !== confirmPassword) {
        setStatus({ type: "error", message: "Passwords do not match." });
        return;
      }

      setSubmitting(true);
      setStatus({ type: "loading", message: "Saving password..." });

      try {
        await axios.post(
          appUrl("auth/invite-password"),
          { token, password },
          {
            headers: { "Content-Type": "application/json" },
            withCredentials: false,
          }
        );

        setStatus({ type: "success", message: "Password set successfully. Redirecting to login..." });
        setTimeout(() => navigate("/login", { replace: true }), 1200);
      } catch (err) {
        setStatus({
          type: "error",
          message: err.response?.data?.message || "Could not set password. Please request a new invitation.",
        });
      } finally {
        setSubmitting(false);
      }
    },
    [confirmPassword, navigate, password, passwordPolicyValid, token]
  );

  const canSubmit = status.type === "ready" || status.type === "error";

  return (
    <div className="auth-page-wrapper">
      <div className="auth-center-container">
        <div className="auth-card-wrapper auth-single-card">
          <div className="auth-form-container">
            <div className="auth-header-block">
              <h2>Set your password</h2>
              <p className="auth-subtitle">
                {inviteUser?.email
                  ? `Create a password for ${inviteUser.email}. You will log in after this step.`
                  : "Create your password to finish the invitation."}
              </p>
            </div>

            {status.message ? (
              <p className={`auth-error-message ${status.type === "success" ? "auth-success-message" : ""}`}>
                {status.message}
              </p>
            ) : null}

            {status.type !== "error" || inviteUser ? (
              <form onSubmit={submitPassword}>
                <div className="auth-form-group">
                  <label htmlFor="invite-password">New password</label>
                  <div className="auth-input-wrapper">
                    <input
                      className="auth-form-input"
                      id="invite-password"
                      type="password"
                      value={password}
                      autoComplete="new-password"
                      disabled={submitting || !canSubmit}
                      onChange={(event) => setPassword(event.target.value)}
                    />
                  </div>
                  <div className="auth-password-conditions" aria-live="polite">
                    {passwordConditions.map((condition) => (
                      <div
                        key={condition.key}
                        className={`auth-password-condition ${passwordChecks[condition.key] ? "pass" : "fail"}`}
                      >
                        <span className="auth-password-condition-dot" aria-hidden="true" />
                        <span>{condition.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="auth-form-group">
                  <label htmlFor="invite-confirm-password">Confirm password</label>
                  <div className="auth-input-wrapper">
                    <input
                      className="auth-form-input"
                      id="invite-confirm-password"
                      type="password"
                      value={confirmPassword}
                      autoComplete="new-password"
                      disabled={submitting || !canSubmit}
                      onChange={(event) => setConfirmPassword(event.target.value)}
                    />
                  </div>
                </div>

                <button className="auth-submit-btn" type="submit" disabled={submitting || !canSubmit}>
                  {submitting ? "Saving..." : "Set password"}
                </button>
              </form>
            ) : (
              <button className="auth-submit-btn" type="button" onClick={() => navigate("/login", { replace: true })}>
                Back to login
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
